import { Box, CircularProgress, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useCallback, useEffect, useState } from 'react'

import { fetchIssuePendingReport } from 'api'

/**
 * The pending report — every open issue, centre by category, split at seven
 * days. Satya's format (2026-09-09), carried over from the report the old
 * tracker's sheet produced by hand.
 *
 * ⭐ THE ORDER IS THE ARGUMENT. Backlog first, inflow second, because the point
 * of the report is that old issues outrank new ones: "every day they stay open
 * is a day a centre is operating with a known unresolved issue". Sorting the
 * rows heaviest-first says the same thing again inside each table.
 *
 * ⭐ Read-only, and deliberately not a filterable table. The board at
 * /issue-tracker is where someone works a queue; this answers "where is the
 * tracker backed up" for a person who is not going to click into anything.
 *
 * The grid is wide — one column per category — so it scrolls inside its own
 * container with the centre column pinned. The page itself never scrolls
 * sideways.
 */
const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  head: { marginBottom: theme.spacing(2.5) },
  // ⭐ The scope line. The page used to print "987 pending" under an HQ title
  // and then a grid holding ONE centre — a centre manager read it as HQ data
  // with eleven centres missing. Every number was his centre's; nothing on
  // the page said so. The scope now sits on the line the eye lands on after
  // the title, and again on the total it qualifies.
  scopeLine: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
    fontSize: 15
  },
  scopePill: {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 6,
    fontWeight: 700,
    fontSize: 14
  },
  scopeAll: { background: '#f2ede5', color: '#5f5a52' },
  scopeSome: { background: '#fdeee8', color: '#b8431f' },
  scopeN: { fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  scopeHint: { color: '#8a8177', fontSize: 13 },
  // The backlog / this-week switch. Same control, same styles, as the
  // board's status row (IssueTracker.js STATUS_TABS) — someone who has used
  // the board knows what this does before reading it, and the count sits in
  // the control so the split is visible without switching.
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2)
  },
  tab: {
    borderRadius: 6,
    padding: theme.spacing(0.75, 1.5),
    fontSize: 14,
    cursor: 'pointer',
    userSelect: 'none',
    border: '1px solid transparent'
  },
  tabOn: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 600
  },
  tabOff: { color: '#5f5a52', border: '1px solid #e3ddd3' },
  count: { opacity: 0.65, marginLeft: 6 },
  kpis: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: theme.spacing(1.75),
    marginBottom: theme.spacing(2.5),
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr 1fr' }
  },
  card: {
    background: '#fff',
    border: '1px solid #e3ddd3',
    borderRadius: 10,
    padding: theme.spacing(2, 2.25)
  },
  k: {
    fontSize: 11.5,
    letterSpacing: '0.06em',
    textTransform: 'lowercase',
    color: '#8a8177',
    fontWeight: 700
  },
  big: {
    fontSize: 30,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    marginTop: 2
  },
  hot: { color: '#f25e35' },
  section: { marginBottom: theme.spacing(3.5) },
  cap: { fontSize: 14, fontWeight: 700, marginBottom: theme.spacing(1.25) },
  capSub: { fontWeight: 400, color: '#8a8177' },
  scroller: {
    overflowX: 'auto',
    border: '1px solid #e3ddd3',
    borderRadius: 10,
    background: '#fff'
  },
  // ⛔ tableLayout MUST stay 'fixed'. Left to itself the browser sizes each
  // column to its HEADER, so "Facilities — repairs, maintenance,
  // infrastructure" became five times the width of PUDO to hold a single
  // digit, and the grid read as scattered. Fixed layout + a colgroup means the
  // DATA sets the width and a long name wraps instead of stretching.
  table: {
    borderCollapse: 'collapse',
    width: '100%',
    tableLayout: 'fixed',
    fontVariantNumeric: 'tabular-nums',
    fontSize: 13.5
  },
  // Wide enough for the longest real header word — PROCUREMENT, DASHBOARDS —
  // to sit on one line at this size. Everything the grid holds is one or two
  // digits, so the header, not the data, is what sets this number.
  // ⛔ The centre column is FIXED. It used to be the only auto-width column, so
  // it swallowed every spare pixel — a 300px lane holding "Sarjapur" while the
  // numbers were crushed together on the right. Now the slack is shared out
  // across the data columns, which is where the reader is actually looking.
  centreCol: { width: 176 },
  numCol: { width: 96 },
  totalColWidth: { width: 84 },
  th: {
    padding: theme.spacing(1.25, 0.75, 1),
    textAlign: 'right',
    fontSize: 10,
    lineHeight: 1.3,
    // ⛔ Keep this. A fixed column is 78px and "INFRASTRUCTURE," is wider than
    // that at any weight, so without it the word escapes the cell and prints
    // over the next header — the same escape PR #860 fixed in the Issue cell.
    // `short_name` usually spares us the long ones; this is what holds when a
    // category is renamed to something long and nobody re-reads this file.
    overflowWrap: 'anywhere',
    letterSpacing: '0.02em',
    textTransform: 'lowercase',
    color: '#8a8177',
    fontWeight: 700,
    borderBottom: '1px solid #e3ddd3',
    verticalAlign: 'bottom'
  },
  td: {
    padding: theme.spacing(1, 1),
    textAlign: 'right',
    whiteSpace: 'nowrap',
    background: '#fff',
    borderBottom: '1px solid #f2ede5'
  },
  firstCol: {
    textAlign: 'left',
    paddingLeft: theme.spacing(2),
    position: 'sticky',
    left: 0,
    zIndex: 1
  },
  // Pinned right, like the centre column is pinned left: with every category
  // on screen the grid scrolls sideways, and the Total is the number the
  // reader came for (Satya, 2026-09-28: "hidden right now").
  totalCol: {
    fontWeight: 700,
    borderLeft: '1px solid #e3ddd3',
    position: 'sticky',
    right: 0,
    zIndex: 1,
    background: '#fff'
  },
  footRow: { fontWeight: 700, borderTop: '2px solid #2b2b2b' },
  owner: {
    display: 'block',
    fontSize: 10,
    letterSpacing: 0,
    textTransform: 'none',
    color: '#a49a8e',
    fontWeight: 400,
    marginTop: 2
  },
  zero: { color: '#cfc7bb' },
  // A pinned header cell must be opaque or the headers scroll under it.
  thPinned: { background: '#fff' },
  // Declared AFTER `td` on purpose: same specificity, so source order decides
  // and the stripe wins. Every cell in the row takes it, the sticky centre
  // column included — a sticky cell must stay opaque or the numbers scroll
  // underneath it.
  stripe: { background: '#faf8f5' },
  // Magnitude, one hue, light to dark. An inset shadow rather than a
  // background so it layers over the stripe instead of fighting it.
  q1: { boxShadow: 'inset 0 0 0 999px rgba(242, 94, 53, 0.07)' },
  q2: { boxShadow: 'inset 0 0 0 999px rgba(242, 94, 53, 0.15)' },
  q3: { boxShadow: 'inset 0 0 0 999px rgba(242, 94, 53, 0.26)' },
  q4: { boxShadow: 'inset 0 0 0 999px rgba(242, 94, 53, 0.4)' },
  scale: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: theme.spacing(1),
    fontSize: 11,
    color: '#8a8177'
  },
  swatch: { width: 26, height: 11, borderRadius: 2, border: '1px solid #e3ddd3' },
  // Bars, not four loose numbers: the question this answers is "what SHAPE is
  // the backlog", and four figures side by side make the reader do the
  // comparison the chart can just show.
  ageStrip: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    maxWidth: 660
  },
  ageRow: {
    display: 'grid',
    gridTemplateColumns: '110px 1fr 48px',
    alignItems: 'center',
    gap: theme.spacing(1.5)
  },
  ageLabel: { fontSize: 12, color: '#5f5a52', textAlign: 'right' },
  ageTrack: {
    display: 'block',
    height: 16,
    background: '#f2ede5',
    borderRadius: 3,
    overflow: 'hidden'
  },
  // ⛔ display:block. These are <span>s, and an inline box ignores both height
  // and a percentage width — the bar rendered as an empty grey track.
  ageFill: {
    display: 'block',
    height: '100%',
    background: '#f25e35',
    borderRadius: 3
  },
  ageN: { fontSize: 13.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  worst: { color: '#d03b3b' },
  empty: { padding: theme.spacing(4), textAlign: 'center', color: '#8a8177' }
}))

// Which grid(s) are on screen. Backlog first and by default, because the
// point of the report is that old issues outrank new ones.
// Two views, not three: "Both" went on 2026-09-22 (Satya, HQ item d).
const VIEWS = [
  { value: 'backlog', label: 'Backlog', countKey: 'backlog' },
  { value: 'fresh', label: 'This week', countKey: 'fresh' }
]

/** "Indiranagar", "Indiranagar and Whitefield", or "4 centres" — the centres
 * the numbers on this page were computed over. `scope` comes from the API,
 * which resolved it from the SAME lookup it filtered the queryset with, so
 * the label cannot disagree with the data. */
const scopeName = (scope) => {
  if (!scope || scope.all) return 'All centres'
  const names = scope.centres || []
  if (!names.length) return 'Your centres'
  if (names.length === 1) return names[0]
  if (names.length <= 3) {
    return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
  }
  return `${names.length} centres`
}

/** The same scope as it reads inside a KPI label: "indiranagar",
 * "2 centres", "all centres". */
const scopeShort = (scope) => {
  if (!scope || scope.all) return 'all centres'
  const names = scope.centres || []
  if (names.length === 1) return names[0]
  return names.length ? `${names.length} centres` : 'your centres'
}

/** The biggest single cell in a table, which the tint is scaled against.
 * Relative, not absolute: the backlog's heaviest cell is 61 and the week's is
 * 8, and both grids should read at full range. */
const peak = (table) => {
  let top = 0
  table.rows.forEach((row) => {
    Object.values(row.cells).forEach((n) => {
      top = Math.max(top, n || 0)
    })
  })
  return top || 1
}

/** Which tint band a cell falls in. Below 5% of the peak nothing is painted —
 * a wash under every 1 and 2 would make the whole grid look busy and tell the
 * reader nothing about where the work actually is. */
const band = (value, top, classes) => {
  const share = (value || 0) / top
  if (share >= 0.6) return classes.q4
  if (share >= 0.35) return classes.q3
  if (share >= 0.15) return classes.q2
  if (share >= 0.05) return classes.q1
  return ''
}

/** A centre x category table. Renders nothing when the split is empty — an
 * empty grid of dashes reads as a broken page rather than as good news. */
const Grid = ({ table, owners, shortNames, classes, emptyText }) => {
  if (!table || !table.total) {
    return (
      <div className={classes.scroller}>
        <Typography className={classes.empty}>{emptyText}</Typography>
      </div>
    )
  }
  const top = peak(table)
  // A category with nothing pending in THIS table is a column of dashes, so
  // it is left out (Satya, 2026-09-28) — no switch to bring it back.
  const columns = table.columns.filter((name) => table.column_totals[name])
  return (
    <div className={classes.scroller}>
      <table className={classes.table}>
        <colgroup>
          {/* The centre column takes whatever is left, so the numeric columns
              stay the same width as each other whatever the header says. */}
          <col className={classes.centreCol} />
          {columns.map((name) => (
            <col className={classes.numCol} key={name} />
          ))}
          <col className={classes.totalColWidth} />
        </colgroup>
        <thead>
          <tr>
            <th
              className={`${classes.th} ${classes.firstCol} ${classes.thPinned}`}
            >
              Centre
            </th>
            {columns.map((name) => (
              <th className={classes.th} key={name}>
                {shortNames[name] || name}
                {owners[name] ? (
                  <span className={classes.owner}>{owners[name]}</span>
                ) : null}
              </th>
            ))}
            <th className={`${classes.th} ${classes.totalCol}`}>Total</th>
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, i) => {
            const stripe = i % 2 ? classes.stripe : ''
            return (
              <tr key={row.centre}>
                <td className={`${classes.td} ${classes.firstCol} ${stripe}`}>
                  {row.centre}
                </td>
                {columns.map((name) => (
                  <td
                    className={`${classes.td} ${stripe} ${
                      row.cells[name]
                        ? band(row.cells[name], top, classes)
                        : classes.zero
                    }`}
                    key={name}
                  >
                    {row.cells[name] || '—'}
                  </td>
                ))}
                <td
                  className={`${classes.td} ${classes.totalCol} ${stripe}`}
                >
                  {row.total || '—'}
                </td>
              </tr>
            )
          })}
        </tbody>
        <tfoot>
          <tr className={classes.footRow}>
            <td className={`${classes.td} ${classes.firstCol}`}>Total</td>
            {columns.map((name) => (
              <td
                className={`${classes.td} ${
                  table.column_totals[name] ? '' : classes.zero
                }`}
                key={name}
              >
                {table.column_totals[name] || '—'}
              </td>
            ))}
            <td className={`${classes.td} ${classes.totalCol}`}>
              {table.total}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

/** The key for the tint. Without it the shading is decoration; with it, it is
 * a scale someone can read. */
const Scale = ({ classes }) => (
  <div className={classes.scale}>
    <span>Fewer</span>
    <span className={`${classes.swatch} ${classes.q1}`} />
    <span className={`${classes.swatch} ${classes.q2}`} />
    <span className={`${classes.swatch} ${classes.q3}`} />
    <span className={`${classes.swatch} ${classes.q4}`} />
    <span>More pending</span>
  </div>
)

const IssuePendingReport = () => {
  const classes = useStyles()
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [view, setView] = useState('backlog')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setReport(await fetchIssuePendingReport())
      setError('')
    } catch (e) {
      setError('Could not load the report. Refresh to try again.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  if (loading) {
    return (
      <Box className={classes.root} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    )
  }
  if (error || !report) {
    return (
      <Box className={classes.root}>
        <Typography>{error || 'No report.'}</Typography>
      </Box>
    )
  }

  const days = report.backlog_days
  // "21 Sep" — the date the two tables split on, so neither heading reads
  // like a rolling window (Satya, 2026-09-28).
  const cutoffLabel = report.cutoff_date
    ? new Date(`${report.cutoff_date}T00:00:00`).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short'
      })
    : `${days} days ago`
  const scope = report.scope || { all: true, centres: [] }
  const showBacklog = view !== 'fresh'
  const showFresh = view !== 'backlog'
  const viewCounts = {
    backlog: report.backlog.total,
    fresh: report.fresh.total
  }
  const ageing = report.ageing || []
  // Bars are scaled against the fattest band, not against the backlog total —
  // otherwise every bar is a stub and the shape, which is the whole point of
  // this strip, disappears.
  const widest = Math.max(1, ...ageing.map((b) => b.count))

  return (
    <Box className={classes.root}>
      <Box className={classes.head}>
        <Typography variant="h5">
          <b>Central Issue Tracker — Pending Report</b>
        </Typography>
        <div className={classes.scopeLine}>
          <span
            className={`${classes.scopePill} ${
              scope.all ? classes.scopeAll : classes.scopeSome
            }`}
            title={scope.all ? '' : (scope.centres || []).join(', ')}
          >
            {scopeName(scope)}
          </span>
          <span>
            <span className={classes.scopeN}>
              {report.total_pending.toLocaleString('en-IN')}
            </span>{' '}
            pending
          </span>
          {scope.all ? null : (
            <span className={classes.scopeHint}>
              · only the centres mapped to you
            </span>
          )}
        </div>
      </Box>

      <div className={classes.kpis}>
        <div className={classes.card}>
          <div className={classes.k}>Total pending · {scopeShort(scope)}</div>
          <div className={`${classes.big} ${classes.hot}`}>
            {report.total_pending.toLocaleString('en-IN')}
          </div>
        </div>
        <div className={classes.card}>
          <div className={classes.k}>Backlog &gt;{days}d</div>
          <div className={classes.big}>
            {report.backlog.total.toLocaleString('en-IN')}
          </div>
        </div>
        <div className={classes.card}>
          <div className={classes.k}>Fresh ≤{days}d</div>
          <div className={classes.big}>{report.fresh.total}</div>
        </div>
        <div className={classes.card}>
          <div className={classes.k}>Centres</div>
          <div className={classes.big}>{report.backlog.rows.length}</div>
        </div>
        <div className={classes.card}>
          <div className={classes.k}>Oldest open</div>
          <div className={classes.big}>
            {report.oldest_open_days}
            <span className={classes.k}> days</span>
          </div>
        </div>
      </div>

      <div className={classes.bar}>
        {VIEWS.map((tab) => (
          <div
            key={tab.value}
            role="button"
            tabIndex={0}
            className={`${classes.tab} ${
              view === tab.value ? classes.tabOn : classes.tabOff
            }`}
            onClick={() => setView(tab.value)}
            onKeyPress={() => setView(tab.value)}
          >
            {tab.label}
            {tab.countKey ? (
              <span className={classes.count}>
                {viewCounts[tab.countKey].toLocaleString('en-IN')}
              </span>
            ) : null}
          </div>
        ))}
      </div>

      {showBacklog ? (
        <div className={classes.section}>
          <Typography className={classes.cap}>
            Pending, raised before {cutoffLabel}{' '}
            <span className={classes.capSub}>
              the backlog · open more than {days} days
            </span>
          </Typography>
          <Grid
            table={report.backlog}
            owners={report.owners || {}}
            shortNames={report.short_names || {}}
            classes={classes}
            emptyText={`Nothing open beyond ${days} days.`}
          />
          {report.backlog.total ? <Scale classes={classes} /> : null}
        </div>
      ) : null}

      {showFresh ? (
        <div className={classes.section}>
          <Typography className={classes.cap}>
            Pending, raised on or after {cutoffLabel}{' '}
            <span className={classes.capSub}>
              fresh inflow · the last {days} days
            </span>
          </Typography>
          <Grid
            table={report.fresh}
            owners={report.owners || {}}
            shortNames={report.short_names || {}}
            classes={classes}
            emptyText={`Nothing raised in the last ${days} days.`}
          />
        </div>
      ) : null}

      {/* The backlog's shape belongs to the backlog: it goes away with it. */}
      {showBacklog ? (
        <div className={classes.section}>
          <Typography className={classes.cap}>
            How old the backlog is{' '}
            <span className={classes.capSub}>
              the {report.backlog.total.toLocaleString('en-IN')}, by age
            </span>
          </Typography>
          <div className={classes.ageStrip}>
            {ageing.map((bucket) => (
              <div className={classes.ageRow} key={bucket.label}>
                <span className={classes.ageLabel}>{bucket.label}</span>
                <span className={classes.ageTrack}>
                  <span
                    className={classes.ageFill}
                    style={{ width: `${(100 * bucket.count) / widest}%` }}
                  />
                </span>
                <span
                  className={`${classes.ageN} ${
                    bucket.label === 'Over 180 days' && bucket.count
                      ? classes.worst
                      : ''
                  }`}
                >
                  {bucket.count.toLocaleString('en-IN')}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </Box>
  )
}

export default IssuePendingReport
